import React, { useState } from 'react';
import axios from 'axios';
import Icon from '@mdi/react';
import { mdiAlertOutline, mdiLoading, mdiTrashCan } from '@mdi/js';
import Modal from './layout/Modal';
import Botao from './Botao';
import styles from './BotaoExcluirComCascata.module.css';

/**
 * Botão de excluir que mostra, antes de confirmar, o que vai embora junto.
 *
 * Sem isso o operador aprova "tem certeza?" sem saber que está derrubando o
 * hotspot da rede, os port-forwards que apontavam para ela e os
 * redirecionamentos que ficariam sem alvo. O levantamento é o mesmo usado pelo
 * assistente de IA — `services/cascata.ts` no backend.
 */

type TipoRecurso = 'rede_lan' | 'rede_wan' | 'vpn' | 'appliance';

type Dependente = {
    tipo: string;
    id: number;
    nome: string;
    descricao: string;
};

type Dependencias = {
    alvo: { tipo: string; id: number; nome: string };
    dependentes: Dependente[];
    filhos: string[];
    alerta?: string;
    preservados?: string[];
};

const ROTA_POR_TIPO: Record<TipoRecurso, string> = {
    rede_lan: '/api/rede_lan',
    rede_wan: '/api/rede_wan',
    vpn: '/api/vpn',
    appliance: '/api/appliance',
};

const ROTULO_POR_TIPO: Record<TipoRecurso, string> = {
    rede_lan: 'rede LAN',
    rede_wan: 'link WAN',
    vpn: 'VPN',
    appliance: 'appliance',
};

type Props = {
    tipo: TipoRecurso;
    id: number;
    /** Chamado após a exclusão concluir, para a tela recarregar a lista. */
    onExcluido: () => void;
    onErro?: (mensagem: string) => void;
    size?: 'sm' | 'md' | 'lg';
    variant?: 'danger' | 'none-danger' | 'outline-danger';
    icon?: string;
    children?: React.ReactNode;
    disabled?: boolean;
    title?: string;
};

const BotaoExcluirComCascata = ({
    tipo,
    id,
    onExcluido,
    onErro,
    size = 'sm',
    variant = 'none-danger',
    icon = mdiTrashCan,
    children,
    disabled,
    title,
}: Props) => {
    const [aberto, setAberto] = useState(false);
    const [carregando, setCarregando] = useState(false);
    const [excluindo, setExcluindo] = useState(false);
    const [deps, setDeps] = useState<Dependencias | null>(null);
    const [erro, setErro] = useState<string | null>(null);

    const abrir = async () => {
        setAberto(true);
        setCarregando(true);
        setDeps(null);
        setErro(null);
        try {
            const res = await axios.get(`${ROTA_POR_TIPO[tipo]}/${id}/dependencias`, {
                withCredentials: true,
            });
            setDeps(res.data?.result ?? null);
        } catch (err: any) {
            setErro(err?.response?.data?.msg ?? 'Não foi possível levantar o que depende deste item.');
        } finally {
            setCarregando(false);
        }
    };

    const confirmar = async () => {
        setExcluindo(true);
        try {
            await axios.delete(`${ROTA_POR_TIPO[tipo]}/${id}?cascata=true`, {
                withCredentials: true,
            });
            setAberto(false);
            onExcluido();
        } catch (err: any) {
            onErro?.(err?.response?.data?.msg ?? 'Falha ao excluir');
        } finally {
            setExcluindo(false);
        }
    };

    const totalItens = (deps?.dependentes.length ?? 0) + 1;

    return (
        <>
            <Modal
                show={aberto}
                title={`Excluir ${ROTULO_POR_TIPO[tipo]}`}
                size={deps?.dependentes.length ? 'md' : 'sm'}
                onClose={() => setAberto(false)}
                onCancel={() => setAberto(false)}
                onConfirm={confirmar}
                confirmProps={{
                    variant: 'danger',
                    label: excluindo
                        ? 'Excluindo…'
                        : totalItens > 1
                            ? `Excluir ${totalItens} itens`
                            : 'Excluir',
                    disabled: carregando || excluindo || !!erro,
                }}
                cancelProps={{ label: 'Cancelar' }}
            >
                {carregando && (
                    <div className={styles.carregando}>
                        <Icon path={mdiLoading} size={1} spin />
                        <span>Verificando o que depende deste item…</span>
                    </div>
                )}

                {erro && <div className={styles.erro}>{erro}</div>}

                {deps && !carregando && (
                    <div className={styles.conteudo}>
                        <p className={styles.alvo}>
                            Excluir {ROTULO_POR_TIPO[tipo]} <strong>{deps.alvo.nome}</strong>?
                        </p>

                        {deps.alerta && (
                            <div className={styles.alerta}>
                                <Icon path={mdiAlertOutline} size={0.8} />
                                <span>{deps.alerta}</span>
                            </div>
                        )}

                        {deps.dependentes.length > 0 && (
                            <div className={styles.bloco}>
                                <span className={styles.blocoTitulo}>
                                    Isto sai junto, senão ficaria órfão:
                                </span>
                                <ul className={styles.lista}>
                                    {deps.dependentes.map((d) => (
                                        <li key={`${d.tipo}-${d.id}`}>{d.descricao}</li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        {deps.filhos.length > 0 && (
                            <div className={styles.bloco}>
                                <span className={styles.blocoTitulo}>Também são removidos:</span>
                                <ul className={styles.lista}>
                                    {deps.filhos.map((f) => (
                                        <li key={f}>{f}</li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        {deps.preservados?.length ? (
                            <div className={styles.bloco}>
                                <span className={styles.blocoTitulo}>
                                    Fica de fora, mas precisa de ajuste manual:
                                </span>
                                <ul className={styles.lista}>
                                    {deps.preservados.map((p) => (
                                        <li key={p}>{p}</li>
                                    ))}
                                </ul>
                            </div>
                        ) : null}

                        {!deps.dependentes.length && !deps.filhos.length && (
                            <p className={styles.semDependencia}>Nada depende deste item.</p>
                        )}
                    </div>
                )}
            </Modal>

            <Botao
                variant={variant}
                size={size}
                icon={icon}
                disabled={disabled}
                title={title}
                onClick={abrir}
            >
                {children}
            </Botao>
        </>
    );
};

export default BotaoExcluirComCascata;
